/**
 * pp-units.js — Toggle de unidades cm ⇄ pulgadas para las medidas del theme.
 *
 * Cómo funciona:
 *   - Cada medida que renderiza el theme se envuelve en <span class="pp-measure">
 *     con su texto original en cm (ej. "30 × 20 × 15 cm" o "48 mm").
 *   - Este script guarda ese texto cm (data-cm) y, cuando el usuario cambia a
 *     pulgadas, parsea los números y los convierte (in = cm ÷ 2.54, mm ÷ 25.4).
 *   - La preferencia vive en localStorage (pp_measurement_unit); default 'cm'.
 *   - El control vive en el header ([data-pp-unit-toggle] con botones
 *     [data-pp-unit-btn="cm"|"in"]).
 *
 * Solo toca elementos .pp-measure (envueltos explícitamente), así que NO
 * convierte "cm" que no sean medidas de producto.
 *
 * API global: window.ppUnits.get() · .set(unit) · .apply(root) — apply() re-pinta
 * medidas de contenido inyectado dinámicamente · .convert(txt) convierte un
 * string cm/mm a la unidad activa sin tocar el DOM.
 */
(function () {
  'use strict';

  var KEY = 'pp_measurement_unit';
  var CM_PER_IN = 2.54;
  var MM_PER_IN = 25.4;

  function read() {
    try { return localStorage.getItem(KEY); } catch (e) { return null; }
  }
  function write(v) {
    try { localStorage.setItem(KEY, v); } catch (e) { /* incógnito / lleno */ }
  }
  function current() { return read() === 'in' ? 'in' : 'cm'; }

  // Redondeo bonito: 1 decimal, sin ".0" sobrante.
  function fmt(n) {
    var r = Math.round(n * 10) / 10;
    return (r % 1 === 0) ? String(r) : r.toFixed(1);
  }

  // "30 × 20 × 15 cm" -> "11.8 × 7.9 × 5.9 in"  ·  "48 mm" -> "1.9 in"
  function toInch(cmText) {
    // Doble unidad ya escrita en el dato (ej. "6\" (152.4 mm)" o "30.5 cm (12\")"):
    // NO tocar — convertir garabatearía ambos números. Eso se arregla limpiando
    // el metafield, no aquí. Ver docs/toggle-unidades-medida.md.
    if (/\(/.test(cmText)) return cmText;
    if (!/(cm|mm)(?![a-zñ])/i.test(cmText)) return cmText; // sin unidad convertible
    var factor = /mm(?![a-zñ])/i.test(cmText) ? MM_PER_IN : CM_PER_IN;
    var out = cmText.replace(/\d+(?:[.,]\d+)?/g, function (num) {
      return fmt(parseFloat(num.replace(',', '.')) / factor);
    });
    return out.replace(/(cm|mm)(?![a-zñ])/gi, 'in');
  }

  function applyOne(el, unit) {
    if (!el.hasAttribute('data-cm')) {
      el.setAttribute('data-cm', (el.textContent || '').trim());
    }
    var cmText = el.getAttribute('data-cm');
    el.textContent = (unit === 'in') ? toInch(cmText) : cmText;
  }

  function applyAll(root, unit) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('.pp-measure');
    for (var i = 0; i < nodes.length; i++) applyOne(nodes[i], unit);
  }

  function syncToggles(unit) {
    // Marca la unidad activa en <html> para que el CSS pueda reaccionar
    // (ej. mostrar una nota en el carrito solo cuando está en pulgadas).
    document.documentElement.setAttribute('data-pp-unit', unit);
    var btns = document.querySelectorAll('[data-pp-unit-btn]');
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i].getAttribute('data-pp-unit-btn') === unit;
      btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
    }
  }

  window.ppUnits = {
    get: current,
    set: function (unit) {
      unit = (unit === 'in') ? 'in' : 'cm';
      write(unit);
      applyAll(document, unit);
      syncToggles(unit);
      document.dispatchEvent(new CustomEvent('pp:unit-change', { detail: { unit: unit } }));
    },
    apply: function (root) { applyAll(root, current()); },
    // Convierte un texto cm/mm a la unidad activa SIN tocar el DOM. Para JS
    // que pinta medidas dinámicamente (ej. hover de variantes en el PDP).
    convert: function (cmText) { return current() === 'in' ? toInch(cmText) : cmText; }
  };

  function boot() {
    applyAll(document, current());
    syncToggles(current());
    // Delegación: un solo listener para todos los toggles del header.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-pp-unit-btn]');
      if (btn) window.ppUnits.set(btn.getAttribute('data-pp-unit-btn'));
    });
    // Contenido inyectado por AJAX (carrito, filtros de colección, búsqueda,
    // recomendaciones): re-pinta a la unidad activa las medidas nuevas que
    // aparezcan, sin que cada feature tenga que llamar ppUnits.apply().
    if (window.MutationObserver && document.body) {
      var mo = new MutationObserver(function (muts) {
        var unit = current();
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (!n || n.nodeType !== 1) continue;
            if (n.classList && n.classList.contains('pp-measure')) applyOne(n, unit);
            if (n.querySelectorAll) {
              var inner = n.querySelectorAll('.pp-measure');
              for (var k = 0; k < inner.length; k++) applyOne(inner[k], unit);
            }
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
